/*
 * RouteDrillDown
 *
 * Purpose:
 * - Render the vessel -> voyage -> route tree and handle its
 *   selection/expansion interactions, emitting selected route IDs upward.
 *
 * History:
 * - Extracted from the former right-docked RouteTreePanel. The panel shell
 *   (Paper, resize handle, width/maxHeight, positioning) now lives in the
 *   host panel (VesselPanel); this component renders only the tree content.
 *
 * Data flow:
 * - Loads vessels, voyages, and routes from core API.
 * - Uses shared vessel last-position map from parent for recenter behavior.
 * - Uses treeSelectionReducer/selectors for local tree UI state.
 * - Selection lives in the tree reducer; map-level logic reads it from there.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import NotificationSnackbar from "@/components/ui/NotificationSnackbar";
import { PANEL_CARD_SX, ROW_DESCRIPTION_SX } from "@/theme/panelStyles";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import SwipeRightAltIcon from "@mui/icons-material/SwipeRightAlt";
import TimelineIcon from "@mui/icons-material/Timeline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningAmberIcon from "@mui/icons-material/Warning";
import HistoryIcon from "@mui/icons-material/History";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { useAccessContext } from "@/contexts/AccessContext";
import { coreGet } from "@/services/coreApi";
import { treeSelectionActions } from "@/features/routeTree/state/VesselTreeState.jsx";
import {
  isDraftSelectionId,
  isReportedSelectionId,
  reportedSelectionId,
  useRouteStore,
} from "@/features/route/state/RouteStore.jsx";
import {
  filterVesselsByName,
  selectRoutesForVoyage,
  selectTreeVessels,
  selectVoyagesForVessel,
} from "@/features/route/state/RouteSelectors.jsx";
import { isRouteSelected } from "@/features/routeTree/state/VesselTreeSelectors.jsx";
import { isActiveEntity } from "@/features/route/state/RouteState.jsx";
import RouteActionsMenu from "@/features/routeTree/RouteActionsMenu.jsx";
import VoyageActionsMenu from "@/features/routeTree/VoyageActionsMenu.jsx";
import { VOYAGE_MONTHS_STEP } from "@/features/routeTree/hooks/useRouteTreeData.jsx";
import { NO_VOYAGE_DRILL_ID } from "@/features/routeTree/hooks/useTreeSelection.jsx";
import RouteTreeToolbar from "@/features/routeTree/RouteTreeToolbar.jsx";
import DrillRow from "@/features/routeTree/DrillRow.jsx";
import RouteRowCard from "@/features/routeTree/RouteRowCard.jsx";
import { routeStaleness, stalenessReference } from "@/features/route/routeStaleness";
import { useWeatherRun } from "@/features/weatherRun";
import useMinuteTick from "@/hooks/useMinuteTick";
import StatusBadge from "@/components/ui/StatusBadge";
import DrillHeader from "@/features/routeTree/DrillHeader.jsx";
import NewVoyageCard from "@/components/ui/NewVoyageCard.jsx";
import DrillListTitle from "@/features/routeTree/DrillListTitle.jsx";
import FleetOverviewCard from "@/features/routeTree/FleetOverviewCard.jsx";
import VesselSpecsView from "@/features/routeTree/VesselSpecsView.jsx";
import { vesselOptionToMeta } from "@/features/vessel/vesselMeta.js";
import {
  useRouteTreeSyncEffects,
  useRouteTreeActions,
} from "@/features/routeTree/hooks/index.jsx";
import exportRouteToFile from "@/utils/exportRouteToFile.jsx";
import { plannedWaypointsToFC } from "@/utils/RouteProcessing";
import { onMouseLeave } from "@/utils/mapUx";
import RouteHelper from "@/utils/routeHelper";
import { pointFeatureMs } from "@/utils/vesselHelper";
import { formatUpdatedUtc, formatUtcShort, routeUpdatedAt } from "@/utils/timeLabels";
import { CAUTION, DANGER } from "@/theme/tokens";

function getRouteDisplayName(route) {
  return route?.name ?? `Route ${route?.id ?? ""}`;
}

// Flat near-white sub-card — kept clean so the only saturated colour in the
// drill area is the action buttons and the selected-row accent bar.
const CARD_SX = {
  ...PANEL_CARD_SX,
  "&:not(:last-of-type)": { mb: 1.25 },
};

// Scrolling region for a drill list — fills the space below the fixed
// header rows inside Card 2 (so only the rows scroll, not the header).
const LIST_SCROLL_SX = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  overscrollBehavior: "contain",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0, 79, 125, 0.25)",
    borderRadius: 3,
  },
};

const SECTION_HEADER_SX = {
  px: 1,
  py: 0.5,
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  color: "#8a98a6",
};

export default function RouteDrillDown({
  isOpen,
  treeState,
  treeDispatch,
  drillPath,
  setDrillPath,
  drillRequest,
  clearDrillRequest,
  treeData,
  vesselsLoading = false,
  vesselsError = "",
  draftRoutes,
  draftAutoAppliedRef: draftAutoAppliedRefProp,
  autoDrilledDraftKeysRef: autoDrilledDraftKeysRefProp,
  autoDrilledVoyagelessKeysRef: autoDrilledVoyagelessKeysRefProp,
  autoDrilledImportKeyRef: autoDrilledImportKeyRefProp,
  lastAppliedImportRouteIdRef: lastAppliedImportRouteIdRefProp,
  selectedDraftRouteIds,
  onToggleDraftSelected,
  onDiscardDraft,
  onRenameDraft,
  onSaveDraft,
  markedVesselNames,
  setMarkedVesselNames,
  recenterToVesselLastPosition,
  recenterToRoute,
  switchVesselFiltersActive,
  switchFleetPlan,
  onToggleFleetPlan,
  fetchingFleetPlan,
  fleetPlanSummary,
  fetchingReported,
  reportedWindowDays,
  vesselReportedRoutes,
  onImportDraft,
  onImportSelectionHint,
  onPlanVoyage,
  onRouteDeleted,
  onVoyageDeleted,
  onRouteStateChanged,
  onRecalculateRoute,
  recalculatingRouteId,
  recalcErrorByRouteId,
  onClearRecalcError,
  onOpenRouteTable,
  onCardClose,
}) {
  const map = useMap();
  const { accessContext, setAccessContext } = useAccessContext();
  const { state: routeStoreState } = useRouteStore();

  // Specs view (info icon). Vessel change or search closes it (the search
  // forces the vessel list; a stale specs state must not pop back open).
  // Deliberately NOT keyed on the drill level: an async auto-drill would
  // otherwise discard an in-progress inline edit.
  const [showVesselSpecs, setShowVesselSpecs] = useState(false);
  useEffect(() => {
    setShowVesselSpecs(false);
  }, [drillPath.vesselId, treeState.ui.vesselSearchTerm]);
  // Expanded route cards; a vessel or voyage change collapses them.
  const [expandedRouteIds, setExpandedRouteIds] = useState(() => new Set());
  // Staleness is derived at render time; tick so it flips without a data change.
  useMinuteTick();
  const toggleRouteExpanded = useCallback((routeId) => {
    setExpandedRouteIds((prev) => {
      const next = new Set(prev);
      if (next.has(routeId)) next.delete(routeId);
      else next.add(routeId);
      return next;
    });
  }, []);
  useEffect(() => {
    setExpandedRouteIds(new Set());
  }, [drillPath.vesselId, drillPath.voyageId]);
  // Parent-owned refs survive this component's unmount on panel switches.
  const localDraftAutoAppliedRef = useRef(new Set());
  const localAutoDrilledDraftKeysRef = useRef(new Set());
  const localAutoDrilledVoyagelessKeysRef = useRef(new Set());
  const localLastAppliedImportRouteIdRef = useRef(null);
  const draftAutoAppliedRef = draftAutoAppliedRefProp ?? localDraftAutoAppliedRef;
  const autoDrilledDraftKeysRef =
    autoDrilledDraftKeysRefProp ?? localAutoDrilledDraftKeysRef;
  const autoDrilledVoyagelessKeysRef =
    autoDrilledVoyagelessKeysRefProp ?? localAutoDrilledVoyagelessKeysRef;
  const localAutoDrilledImportKeyRef = useRef(null);
  const autoDrilledImportKeyRef =
    autoDrilledImportKeyRefProp ?? localAutoDrilledImportKeyRef;
  const lastAppliedImportRouteIdRef =
    lastAppliedImportRouteIdRefProp ?? localLastAppliedImportRouteIdRef;
  const autoExpandedVesselIdsRef = useRef(new Set());
  const contentRef = useRef(null);
  // Kebab menu of the "New voyage" placeholder row (delete/discard only).
  const [noVoyageMenuAnchorEl, setNoVoyageMenuAnchorEl] = useState(null);

  const noop = useCallback(() => {}, []);

  const {
    voyagesLoadingByVesselId,
    voyagesErrorByVesselId,
    voyageMonthsByVesselId,
    routesLoadingByVoyageId,
    routesErrorByVoyageId,
    routesFetchedByVoyageId,
    loadVoyagesForVessel,
    loadOlderVoyagesForVessel,
    loadRoutesForVoyage,
    upsertVessel,
    upsertVoyageForVessel,
    removeVoyageForVessel,
    archiveOtherActiveVoyages,
    upsertRouteForVoyage,
    removeRouteFromVoyage,
    archiveOtherActivePlannedRoutes,
    archivePlannedRoutesForVoyage,
  } = treeData;

  const weatherStatus = useWeatherRun()?.status ?? null;
  const stalenessRef = useMemo(() => stalenessReference(weatherStatus), [weatherStatus]);
  const vessels = useMemo(
    () => selectTreeVessels(routeStoreState),
    [routeStoreState],
  );

  const {
    routeDeleteCandidate,
    routeDeleteOpen,
    setRouteDeleteOpen,
    setRouteDeleteCandidate,
    routeActionAnchorEl,
    routeActionTarget,
    openDeleteDialog,
    handleOpenRouteActionMenu,
    handleCloseRouteActionMenu,
    handleRouteActionMenuExited,
    handleDeleteRouteConfirm,
    handleSetRouteState,
    handleRenameRoute,
    voyageActionAnchorEl,
    voyageActionTarget,
    handleOpenVoyageActionMenu,
    handleCloseVoyageActionMenu,
    handleSetVoyageState,
    voyageDeleteCandidate,
    voyageDeleteOpen,
    setVoyageDeleteOpen,
    setVoyageDeleteCandidate,
    openVoyageDeleteDialog,
    handleDeleteVoyageConfirm,
    actionError,
    setActionError,
  } = useRouteTreeActions({
    accessContext,
    setAccessContext,
    treeState,
    treeDispatch,
    removeRouteFromVoyage,
    upsertRouteForVoyage,
    archiveOtherActivePlannedRoutes,
    archivePlannedRoutesForVoyage,
    upsertVoyageForVessel,
    removeVoyageForVessel,
    archiveOtherActiveVoyages,
    onDiscardDraft,
    onRenameDraft,
    onRouteDeleted,
    onVoyageDeleted,
    onRouteStateChanged,
  });

  const hasVesselSearchTerm = treeState.ui.vesselSearchTerm.trim().length > 0;

  const filteredVessels = useMemo(
    () => filterVesselsByName(vessels, treeState.ui.vesselSearchTerm),
    [vessels, treeState.ui.vesselSearchTerm],
  );

  const isSelected = useCallback(
    (name) => markedVesselNames?.some((v) => (v?.vesselName ?? "") === name),
    [markedVesselNames],
  );

  // Single-vessel selection: VESSEL_MARKED atomically replaces the previous
  // vessel and clears its selections; re-marking the same vessel is a no-op.
  const markVesselAsActive = useCallback(
    (vessel, { recenter = false } = {}) => {
      const vesselName = vessel?.name ?? "";
      if (!vesselName) return;
      const list = markedVesselNames ?? [];
      // A name-only mark is re-marked so the reducer can pick up the id.
      const alreadyOnlySelected =
        list.length === 1 &&
        (list[0]?.vesselName ?? "") === vesselName &&
        (list[0]?.vesselId != null || vessel?.id == null);
      if (!alreadyOnlySelected) {
        setMarkedVesselNames([{ vesselName, vesselId: vessel?.id ?? null }]);
      }
      if (recenter) {
        recenterToVesselLastPosition?.(vessel?.id);
      }
    },
    [markedVesselNames, setMarkedVesselNames, recenterToVesselLastPosition],
  );

  useRouteTreeSyncEffects({
    onImportSelectionHint,
    treeState,
    treeDispatch,
    upsertVessel,
    upsertVoyageForVessel,
    archiveOtherActiveVoyages,
    archivePlannedRoutesForVoyage,
    upsertRouteForVoyage,
    loadVoyagesForVessel,
    loadRoutesForVoyage,
    setVesselsExpanded: noop,
    lastAppliedImportRouteIdRef,
    draftRoutes,
    draftAutoAppliedRef,
    vessels,
    markedVesselNames,
    markVesselAsActive,
  });

  const markedTreeVesselIds = useMemo(() => {
    const byName = new Map((vessels ?? []).map((v) => [v?.name ?? "", v?.id]));
    return (markedVesselNames ?? [])
      .map((entry) => entry?.vesselId ?? byName.get(entry?.vesselName ?? ""))
      .filter((id) => id != null);
  }, [markedVesselNames, vessels]);

  useEffect(() => {
    if (!isOpen) return;
    const el = contentRef.current;
    if (!el) return;
    L.DomEvent.disableClickPropagation(el);
  }, [isOpen]);

  // Preload voyages for already-marked vessels so their voyage counts show in
  // the Vessels list even before the user drills in.
  useEffect(() => {
    for (const vesselId of markedTreeVesselIds) {
      if (autoExpandedVesselIdsRef.current.has(vesselId)) continue;
      autoExpandedVesselIdsRef.current.add(vesselId);
      if (voyageMonthsByVesselId[vesselId] == null) {
        loadVoyagesForVessel(vesselId);
      }
    }
  }, [markedTreeVesselIds, voyageMonthsByVesselId, loadVoyagesForVessel]);

  // ── Drill navigation ────────────────────────────────────────────────────
  const goToVessels = useCallback(() => {
    // VESSEL_UNMARKED clears mark + selections in one step.
    setMarkedVesselNames([]);
    clearDrillRequest?.();
    setDrillPath({ level: "vessels", vesselId: null, voyageId: null });
  }, [setMarkedVesselNames, clearDrillRequest, setDrillPath]);

  // A vessel with an active route opens straight at that voyage's routes level.
  const drillIntoVessel = useCallback(
    async (vessel, { recenter = true } = {}) => {
      const vesselId = vessel?.id ?? vessel?.name;
      if (vesselId == null) return;
      const activeVoyageId = Number(
        fleetPlanSummary?.routeByVesselId?.[Number(vessel?.id)]?.voyageId,
      );
      const jumpToRoutes = Number.isFinite(activeVoyageId) && activeVoyageId > 0;
      setDrillPath(
        jumpToRoutes
          ? { level: "routes", vesselId, voyageId: activeVoyageId }
          : { level: "voyages", vesselId, voyageId: null },
      );
      markVesselAsActive(vessel, { recenter });
      await Promise.all([
        voyageMonthsByVesselId[vesselId] == null
          ? loadVoyagesForVessel(vesselId)
          : null,
        jumpToRoutes ? loadRoutesForVoyage(activeVoyageId) : null,
      ]);
    },
    [
      setDrillPath,
      markVesselAsActive,
      fleetPlanSummary,
      voyageMonthsByVesselId,
      loadVoyagesForVessel,
      loadRoutesForVoyage,
    ],
  );

  const goToVoyages = useCallback(() => {
    setDrillPath((prev) => ({
      level: "voyages",
      vesselId: prev.vesselId,
      voyageId: null,
    }));
  }, [setDrillPath]);

  const drillIntoNoVoyage = useCallback(() => {
    setDrillPath((prev) => ({
      level: "routes",
      vesselId: prev.vesselId,
      voyageId: NO_VOYAGE_DRILL_ID,
    }));
  }, [setDrillPath]);

  const drillIntoVoyage = useCallback(
    async (vessel, voyage) => {
      const vesselId = vessel?.id ?? vessel?.name;
      const voyageId = voyage?.id;
      if (voyageId == null) return;
      setDrillPath({ level: "routes", vesselId, voyageId });
      markVesselAsActive(vessel, { recenter: false });
      await loadRoutesForVoyage(voyageId);
    },
    [setDrillPath, markVesselAsActive, loadRoutesForVoyage],
  );

  const reportedAvailable = RouteHelper.reportedRouteExists(
    markedVesselNames,
    vesselReportedRoutes,
  );
  const lastReportedTimestamp = useMemo(() => {
    let latestMs = null;
    for (const route of vesselReportedRoutes ?? []) {
      for (const feature of route?.points?.geojson?.features ?? []) {
        const ms = pointFeatureMs(feature);
        if (ms != null && (latestMs == null || ms > latestMs)) latestMs = ms;
      }
    }
    return latestMs;
  }, [vesselReportedRoutes]);

  // Honour external drill requests (e.g. clicking a vessel marker on the map):
  // resolve the vessel by name and run the normal drill-into-vessel flow.
  const handledDrillNonceRef = useRef(null);
  const syncedDrillVesselRef = useRef(null);
  useEffect(() => {
    if (!drillRequest?.vesselName) return;
    if (handledDrillNonceRef.current === drillRequest.nonce) return;
    const vessel = vessels.find(
      (v) => (v?.name ?? "") === drillRequest.vesselName,
    );
    if (!vessel) return; // vessels not loaded yet; retry once they are
    const vesselId = vessel?.id ?? vessel?.name;
    // Pre-mark so the follow-marked-vessel effect below doesn't drill again.
    syncedDrillVesselRef.current = vesselId;
    if (drillPath.vesselId !== vesselId) {
      drillIntoVessel(vessel, { recenter: drillRequest.recenter !== false });
    }
    // Optionally drill one level deeper into a specific voyage's routes.
    const reqVoyageId = drillRequest.voyageId;
    if (reqVoyageId === NO_VOYAGE_DRILL_ID) {
      setDrillPath({ level: "routes", vesselId, voyageId: NO_VOYAGE_DRILL_ID });
    } else if (reqVoyageId != null) {
      drillIntoVoyage(vessel, { id: reqVoyageId });
      // Ensure, not toggle: an already-selected voyage must stay selected.
      treeDispatch(
        treeSelectionActions.ensureVoyageSelected(
          Number(vesselId),
          Number(reqVoyageId),
        ),
      );
    }
    // Clear once consumed so a remount doesn't re-drill and reset the level.
    handledDrillNonceRef.current = null;
    clearDrillRequest?.();
  }, [
    drillRequest,
    vessels,
    drillPath.vesselId,
    drillIntoVessel,
    drillIntoVoyage,
    setDrillPath,
    treeDispatch,
    clearDrillRequest,
  ]);

  // Follow the marked vessel when it changes externally (e.g. the planner picks
  // a different vessel) so the drill view doesn't stay on the old one.
  useEffect(() => {
    const markedId = markedTreeVesselIds[0] ?? null;
    if (markedId == null) {
      syncedDrillVesselRef.current = null;
      return;
    }
    if (syncedDrillVesselRef.current === markedId) return;
    // A pending voyage-depth drill request owns the drill — but only for the
    // marked vessel; a stale request must not block following forever.
    const marked = (markedVesselNames ?? [])[0];
    if (
      drillRequest?.voyageId != null &&
      drillRequest?.vesselName === (marked?.vesselName ?? "")
    ) {
      return;
    }
    if (drillPath.vesselId === markedId) {
      syncedDrillVesselRef.current = markedId;
      return;
    }
    const vessel = vessels.find((v) => (v?.id ?? v?.name) === markedId);
    // Not loaded yet — stay unsynced so the vessels-loaded re-run drills.
    if (!vessel) return;
    syncedDrillVesselRef.current = markedId;
    // The action that changed the vessel owns the recenter decision.
    drillIntoVessel(vessel, { recenter: false });
  }, [
    markedTreeVesselIds,
    markedVesselNames,
    drillPath.vesselId,
    vessels,
    drillIntoVessel,
    drillRequest,
  ]);

  // Auto-drill to a NEW draft's routes level; a recompute does not re-drill.
  useEffect(() => {
    const keys = new Set();
    let target = null;
    for (const draft of draftRoutes ?? []) {
      const vesselId = Number(draft?.vesselId);
      const rawVoyageId = draft?.voyageId;
      const voyageId = rawVoyageId == null ? NaN : Number(rawVoyageId);
      if (
        !Number.isFinite(vesselId) ||
        !(Number.isFinite(voyageId) && voyageId > 0)
      ) {
        continue;
      }
      const key = `${draft?.routeId}:${vesselId}:${voyageId}`;
      keys.add(key);
      if (!autoDrilledDraftKeysRef.current.has(key)) {
        target = { vesselId, voyageId };
      }
    }
    autoDrilledDraftKeysRef.current = keys;
    if (!target) return;
    setDrillPath({
      level: "routes",
      vesselId: target.vesselId,
      voyageId: target.voyageId,
    });
    loadRoutesForVoyage(target.voyageId);
  }, [draftRoutes, setDrillPath, loadRoutesForVoyage]);

  useEffect(() => {
    const keys = new Set();
    let targetVesselId = null;
    for (const draft of draftRoutes ?? []) {
      const vesselId = Number(draft?.vesselId);
      const vid = draft?.voyageId;
      const hasVoyage =
        vid != null && Number.isFinite(Number(vid)) && Number(vid) > 0;
      if (!Number.isFinite(vesselId) || hasVoyage) continue;
      const key = `${draft?.routeId}:${vesselId}`;
      keys.add(key);
      if (!autoDrilledVoyagelessKeysRef.current.has(key)) {
        targetVesselId = vesselId;
      }
    }
    autoDrilledVoyagelessKeysRef.current = keys;
    if (targetVesselId == null) return;
    setDrillPath({
      level: "routes",
      vesselId: targetVesselId,
      voyageId: NO_VOYAGE_DRILL_ID,
    });
  }, [draftRoutes, setDrillPath]);

  // Auto-drill to the routes level when an import selection hint arrives. The
  // route selection + vessel activation are handled by useRouteTreeSyncEffects.
  // Guard ref is parent-owned: a local one dies with the panel unmount and the
  // still-set hint would re-drill to the import vessel on every remount.
  useEffect(() => {
    const vesselId = Number(onImportSelectionHint?.vesselId);
    const voyageId = Number(onImportSelectionHint?.voyageId);
    if (!Number.isFinite(vesselId) || !Number.isFinite(voyageId)) return;
    const key = `${vesselId}:${voyageId}:${onImportSelectionHint?.routeId ?? ""}`;
    if (autoDrilledImportKeyRef.current === key) return;
    autoDrilledImportKeyRef.current = key;
    setDrillPath({ level: "routes", vesselId, voyageId });
  }, [onImportSelectionHint, setDrillPath, autoDrilledImportKeyRef]);

  const handleRouteClick = useCallback(
    (vessel, voyage, route) => {
      const voyageId = voyage?.id;
      const routeId = route?.id;
      if (!voyageId || !routeId) return;
      const wasSelected = isRouteSelected(treeState, routeId);
      onCardClose?.();

      // Voyage switch: drafts are deselected like DB routes (kept in the store).
      const currentVoyageId = treeState.ui.selectedVoyageIds?.[0] ?? null;
      const isVoyageSwitch =
        currentVoyageId != null && Number(currentVoyageId) !== Number(voyageId);
      if (isVoyageSwitch) {
        for (const selectionId of (treeState.ui.selectedRouteIds ?? []).filter(
          isDraftSelectionId,
        )) {
          treeDispatch(treeSelectionActions.deselectRoute(selectionId));
        }
      }

      markVesselAsActive(vessel, { recenter: false });
      if (!wasSelected) recenterToRoute?.(routeId);
      treeDispatch(
        treeSelectionActions.toggleRouteSelected(vessel?.id, voyageId, routeId),
      );
    },
    [
      treeState,
      treeDispatch,
      markVesselAsActive,
      onCardClose,
      recenterToRoute,
    ],
  );

  const handleDraftClick = useCallback(
    (route) => {
      const selectionId = route?.id;
      const routeId = Number(route?.routeId);
      if (!Number.isFinite(routeId)) return;
      const wasSelected = isRouteSelected(treeState, selectionId);
      // Single dispatch via the drafts hook; parents co-select the voyage.
      onToggleDraftSelected?.(routeId, {
        vesselId: route?.vesselId,
        voyageId: route?.voyageId,
      });
      // Draft geometry lives under its store route id, not the selection id.
      if (!wasSelected) recenterToRoute?.(routeId);
    },
    [treeState, onToggleDraftSelected, recenterToRoute],
  );

  const handleExportRoute = useCallback(
    async (target, options = {}) => {
      if (target?.route?.source === "draft") {
        // Per-draft raw astar response — the global last compute would be
        // wrong when a different draft is exported.
        const rawResponse = target?.route?.rawResponse;
        if (!rawResponse) {
          throw new Error(
            "Cannot export suggested route: missing route payload. Recompute the route and try again.",
          );
        }

        const exportDisplayName =
          target?.vessel?.name ??
          target?.route?.vesselName ??
          options?.routeName?.trim?.() ??
          target?.route?.name ??
          "Converted";
        const vesselId = Number(target?.vessel?.id ?? target?.route?.vesselId);
        const hasVessel = Number.isInteger(vesselId) && vesselId >= 1;
        // Only the voyageFile export reads profiles — skip the fetch otherwise.
        const needsProfiles = (options?.fileFormat ?? "geojson") === "voyageFile";
        let candidateConsumptionProfile = rawResponse?.consumption ?? null;

        if (!candidateConsumptionProfile && hasVessel && needsProfiles) {
          const consumptionProfileResponse = await coreGet(
            `/consumption-profiles?vessel_id=${vesselId}`,
            accessContext,
            setAccessContext,
            ".core /consumption-profiles (candidate tree export) error=",
          );
          candidateConsumptionProfile = consumptionProfileResponse?.profile
            ? {
                ...consumptionProfileResponse.profile,
                name: exportDisplayName,
              }
            : null;
        }

        await exportRouteToFile({
          fileFormat: options?.fileFormat ?? "geojson",
          pathfinderResponse: {
            ...rawResponse,
            consumption:
              rawResponse?.consumption ?? candidateConsumptionProfile,
          },
          selectedRoute:
            options?.routeName?.trim?.() || target?.route?.name || "route",
          selectedVessel: target?.vessel ?? null,
          selectedVoyage: target?.voyage ?? null,
          accessContext,
          setAccessContext,
        });
        return;
      }

      const routeId = Number(target?.route?.id);
      if (!Number.isFinite(routeId)) {
        throw new Error("Cannot export route: missing route id.");
      }

      const data = await coreGet(
        `/waypoints?route_ids=${routeId}`,
        accessContext,
        setAccessContext,
        ".core /waypoints (tree export) error=",
      );

      const rawWaypoints = Array.isArray(data?.waypoints) ? data.waypoints : [];
      if (!rawWaypoints.length) {
        throw new Error("Cannot export route: no waypoints available.");
      }

      const vesselId = Number(target?.vessel?.id ?? target?.route?.vesselId);
      const hasVessel = Number.isInteger(vesselId) && vesselId >= 1;
      const exportDisplayName =
        target?.vessel?.name ??
        target?.route?.vesselName ??
        options?.routeName?.trim?.() ??
        target?.route?.name ??
        "Converted";

      // Only the voyageFile export reads profiles — skip the fetches otherwise.
      const needsProfiles = (options?.fileFormat ?? "geojson") === "voyageFile";
      let speedProfiles = [];
      let consumptionProfile = null;
      if (hasVessel && needsProfiles) {
        const [speedProfileResponse, consumptionProfileResponse] =
          await Promise.all([
            coreGet(
              `/speed-profiles?vessel_id=${vesselId}`,
              accessContext,
              setAccessContext,
              ".core /speed-profiles (tree export) error=",
            ),
            coreGet(
              `/consumption-profiles?vessel_id=${vesselId}`,
              accessContext,
              setAccessContext,
              ".core /consumption-profiles (tree export) error=",
            ),
          ]);
        const speedProfile = speedProfileResponse?.profile;
        speedProfiles = [speedProfile?.wind, speedProfile?.wave].filter(
          Boolean,
        );
        consumptionProfile = consumptionProfileResponse?.profile
          ? {
              ...consumptionProfileResponse.profile,
              name: exportDisplayName,
            }
          : null;
      }

      const plannedRouteFC = plannedWaypointsToFC(
        rawWaypoints,
        speedProfiles,
        consumptionProfile,
        {
          jsonRequest: target?.route?.jsonRequest ?? null,
          summary: target?.route?.summary ?? null,
        },
      );

      await exportRouteToFile({
        fileFormat: options?.fileFormat ?? "geojson",
        pathfinderResponse: plannedRouteFC,
        selectedRoute:
          options?.routeName?.trim?.() || target?.route?.name || "route",
        selectedVessel: target?.vessel ?? null,
        selectedVoyage: target?.voyage ?? null,
        accessContext,
        setAccessContext,
      });
    },
    [accessContext, setAccessContext],
  );

  // ── Derived data for the current drill level ────────────────────────────
  const drilledVessel = useMemo(() => {
    if (drillPath.vesselId == null) return null;
    return (
      vessels.find((v) => (v?.id ?? v?.name) === drillPath.vesselId) ??
      routeStoreState.entities.vesselsById?.[drillPath.vesselId] ??
      null
    );
  }, [drillPath.vesselId, vessels, routeStoreState]);

  // Fetched-gate: the union merge may know children from fleet/waypoint
  // fragments before the tree fetch — don't render those as ghost rows.
  const drilledVoyages = useMemo(() => {
    if (drillPath.vesselId == null) return [];
    if (voyageMonthsByVesselId[drillPath.vesselId] == null) return [];
    return selectVoyagesForVessel(routeStoreState, drillPath.vesselId);
  }, [drillPath.vesselId, voyageMonthsByVesselId, routeStoreState]);

  const drilledVoyage = useMemo(() => {
    if (drillPath.voyageId == null) return null;
    return (
      drilledVoyages.find((v) => v?.id === drillPath.voyageId) ??
      routeStoreState.entities.voyagesById?.[drillPath.voyageId] ??
      null
    );
  }, [drillPath.voyageId, drilledVoyages, routeStoreState]);

  // Voyage name shown at the routes level. A just-created voyage isn't in the
  // loaded lists yet, so fall back to the import hint's name when its id matches
  // the drilled voyage, then to a neutral "Voyage" label.
  const drilledVoyageName = useMemo(() => {
    if (drillPath.voyageId === NO_VOYAGE_DRILL_ID) return "New voyage";
    if (drilledVoyage?.name) return drilledVoyage.name;
    if (
      drillPath.voyageId != null &&
      Number(onImportSelectionHint?.voyageId) === Number(drillPath.voyageId) &&
      onImportSelectionHint?.voyageName
    ) {
      return onImportSelectionHint.voyageName;
    }
    return "Voyage";
  }, [drilledVoyage, drillPath.voyageId, onImportSelectionHint]);

  const activeVoyage = useMemo(
    () =>
      drilledVoyages.find(
        (v) => isActiveEntity(v) && (v?.type ?? "planned") === "planned",
      ) ?? null,
    [drilledVoyages],
  );

  const voyagelessDrafts = useMemo(
    () =>
      (draftRoutes ?? []).filter((draft) => {
        const vid = draft?.voyageId;
        const hasVoyage =
          vid != null && Number.isFinite(Number(vid)) && Number(vid) > 0;
        if (hasVoyage) return false;
        return Number(draft?.vesselId) === Number(drillPath.vesselId);
      }),
    [draftRoutes, drillPath.vesselId],
  );

  // Last voyageless draft gone (discarded/saved): the pseudo-voyage level is
  // empty, fall back to the voyage level. A save's import hint drills to the
  // real voyage in the same commit — recheck prev inside the updater (not the
  // render-closure value) so this fallback doesn't clobber that drill.
  useEffect(() => {
    if (drillPath.voyageId !== NO_VOYAGE_DRILL_ID) return;
    if (voyagelessDrafts.length > 0) return;
    setDrillPath((prev) =>
      prev.voyageId === NO_VOYAGE_DRILL_ID
        ? { level: "voyages", vesselId: prev.vesselId, voyageId: null }
        : prev,
    );
  }, [drillPath.voyageId, voyagelessDrafts, setDrillPath]);

  const drilledRoutes = useMemo(() => {
    if (drillPath.voyageId === NO_VOYAGE_DRILL_ID) return voyagelessDrafts;
    if (drillPath.voyageId == null) return [];
    const routeItems = routesFetchedByVoyageId[drillPath.voyageId]
      ? selectRoutesForVoyage(routeStoreState, drillPath.voyageId)
      : [];
    const draftsForVoyage = (draftRoutes ?? []).filter(
      (draft) => Number(draft?.voyageId) === Number(drillPath.voyageId),
    );
    return [...draftsForVoyage, ...routeItems];
  }, [
    drillPath.voyageId,
    routeStoreState,
    routesFetchedByVoyageId,
    draftRoutes,
    voyagelessDrafts,
  ]);

  // After a recalc the successor appears while its predecessor is still
  // expanded: move the expansion so the fresh numbers are what stays open.
  const seenRouteIdsRef = useRef(new Set());
  useEffect(() => {
    seenRouteIdsRef.current = new Set();
  }, [drillPath.vesselId, drillPath.voyageId]);
  useEffect(() => {
    const seen = seenRouteIdsRef.current;
    // Drafts carry non-numeric ids; they never succeed a persisted route.
    const persisted = drilledRoutes.filter((r) => Number.isFinite(Number(r?.id)));
    const arrivals = persisted.filter((r) => !seen.has(Number(r.id)));
    persisted.forEach((r) => seen.add(Number(r.id)));
    if (arrivals.length === 0) return;
    setExpandedRouteIds((prev) => {
      let next = null;
      for (const route of arrivals) {
        const pred = Number(route?.summary?.predecessor_route_id);
        if (Number.isFinite(pred) && prev.has(pred)) {
          next ??= new Set(prev);
          next.delete(pred);
          next.add(Number(route.id));
        }
      }
      return next ?? prev;
    });
  }, [drilledRoutes]);

  // Archived = state 'archived' (not just active===false; 'suggested' is also that).
  const isRouteArchived = useCallback(
    (route) =>
      route?.state ? route.state === "archived" : route?.active === false,
    [],
  );

  // Null while voyages are loading or only voyageless drafts exist — a "0
  // voyages" line would contradict the visible "New voyage" placeholder row.
  const drilledVoyageMeta = useMemo(() => {
    if (drillPath.level !== "voyages") return null;
    const total = drilledVoyages.length;
    if (total === 0) return null;
    const active = drilledVoyages.filter((v) => !isRouteArchived(v)).length;
    const label = `${total} ${total === 1 ? "voyage" : "voyages"}`;
    return active > 0 ? `${label} · ${active} active` : label;
  }, [drillPath.level, drilledVoyages, isRouteArchived]);

  // Tree reducer is the single source of truth for selection. The reported
  // track is not a route: it neither counts nor feeds the comparison table.
  const selectedRouteSelectionIds = useMemo(
    () =>
      (treeState.ui.selectedRouteIds ?? []).filter(
        (id) => !isReportedSelectionId(id),
      ),
    [treeState.ui.selectedRouteIds],
  );
  const selectedRouteCount = selectedRouteSelectionIds.length;

  // An active search always shows the vessel list, whatever the drill level.
  const displayLevel = hasVesselSearchTerm ? "vessels" : drillPath.level;


  // Name-only vessels (no id) can't have a reported track.
  const drilledVesselId = Number(drilledVessel?.id);
  const drilledVesselHasId =
    Number.isInteger(drilledVesselId) && drilledVesselId > 0;
  const reportedSelection = reportedSelectionId(drilledVesselId);
  const reportedSelected = isRouteSelected(treeState, reportedSelection);
  const reportedSelectable = reportedAvailable && drilledVesselHasId;
  const reportedRow = (
    <DrillRow
      icon={<HistoryIcon sx={{ fontSize: 16 }} />}
      primary={
        reportedWindowDays != null
          ? `${reportedWindowDays}-day report`
          : "Reported track"
      }
      secondary={
        reportedSelectable
          ? formatUtcShort(lastReportedTimestamp)
          : fetchingReported
            ? "Loading reported positions…"
            : "No reported positions"
      }
      badge={<StatusBadge variant="reported" label="Reported" />}
      muted={!reportedSelectable}
      disabled={!reportedSelectable && !reportedSelected}
      selected={reportedSelected}
      showChevron={false}
      onClick={() =>
        treeDispatch(
          treeSelectionActions.toggleRouteSelected(null, null, reportedSelection),
        )
      }
    />
  );

  // Vessels drilled via name fallback have no real id — no specs for those
  // (a save could never be persisted).
  const specsAvailable = drilledVesselHasId;
  const specsVisible =
    showVesselSpecs && specsAvailable && displayLevel !== "vessels";

  // Saved edits land in the RouteStore entity, so the row is always current.
  const drilledVesselMeta = useMemo(
    () =>
      specsAvailable && drilledVessel ? vesselOptionToMeta(drilledVessel) : null,
    [specsAvailable, drilledVessel],
  );

  // Routes and voyages share the lifecycle states.
  const stateBadge = useCallback(
    (entity) => {
      if (entity?.source === "draft")
        return <StatusBadge variant="unsaved" label="Draft" />;
      const state =
        entity?.state ?? (entity?.active === false ? "archived" : "active");
      if (state === "suggested")
        return <StatusBadge variant="suggested" label="Suggested" />;
      if (state === "archived")
        return <StatusBadge variant="archived" label="Archived" />;
      return <StatusBadge variant="active" label="Active" />;
    },
    [],
  );

  const routeHasWarnings = useCallback((route) => {
    const s = route?.summary;
    return (
      (s?.weather_warnings_count ?? 0) > 0 ||
      (s?.rnw_warnings_count ?? 0) > 0 ||
      (s?.hra_warnings_count ?? 0) > 0 ||
      (s?.jwla_warnings_count ?? 0) > 0 ||
      (s?.custom_area_warnings_count ?? 0) > 0
    );
  }, []);

  const routeHasEcaZones = useCallback(
    (route) => (route?.summary?.eca_warnings_count ?? 0) > 0,
    [],
  );

  if (!isOpen) return null;

  return (
    <Box
      ref={contentRef}
      sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
    >
      <RouteActionsMenu
        routeDeleteOpen={routeDeleteOpen}
        routeDeleteCandidate={routeDeleteCandidate}
        getRouteDisplayName={getRouteDisplayName}
        onCloseDeleteDialog={() => {
          setRouteDeleteOpen(false);
          onMouseLeave(map);
        }}
        onDeleteConfirm={() => {
          handleDeleteRouteConfirm();
          onMouseLeave(map);
        }}
        routeActionAnchorEl={routeActionAnchorEl}
        routeActionTarget={routeActionTarget}
        onCloseRouteActionMenu={handleCloseRouteActionMenu}
        onRouteActionMenuExited={handleRouteActionMenuExited}
        onSetRouteState={handleSetRouteState}
        onRenameRoute={handleRenameRoute}
        onSaveDraft={onSaveDraft}
        onExportRoute={handleExportRoute}
        onDialogClose={() => onMouseLeave(map)}
        activeVoyage={activeVoyage}
        onOpenDeleteFromAction={(target) => {
          if (!target) {
            setRouteDeleteCandidate(null);
            return;
          }
          openDeleteDialog(target.vessel, target.voyage, target.route);
        }}
      />

      <VoyageActionsMenu
        voyageActionAnchorEl={voyageActionAnchorEl}
        voyageActionTarget={voyageActionTarget}
        onCloseVoyageActionMenu={handleCloseVoyageActionMenu}
        onSetVoyageState={handleSetVoyageState}
        voyageDeleteOpen={voyageDeleteOpen}
        voyageDeleteCandidate={voyageDeleteCandidate}
        onCloseVoyageDeleteDialog={() => {
          setVoyageDeleteOpen(false);
          onMouseLeave(map);
        }}
        onVoyageDeleteConfirm={() => {
          handleDeleteVoyageConfirm();
          onMouseLeave(map);
        }}
        onOpenVoyageDeleteFromAction={(target) => {
          if (!target) {
            setVoyageDeleteCandidate(null);
            return;
          }
          openVoyageDeleteDialog(target.vessel, target.voyage);
        }}
      />

      {/* ── Card 1: Import + vessel search ─────────────────────────────── */}
      <Box sx={{ ...CARD_SX, flexShrink: 0 }} data-tour="fleet-search">
        <RouteTreeToolbar
          vesselSearchTerm={treeState.ui.vesselSearchTerm}
          onSearchChange={(e) =>
            treeDispatch(
              treeSelectionActions.setVesselSearchTerm(e.target.value),
            )
          }
          onSearchClear={() =>
            treeDispatch(treeSelectionActions.setVesselSearchTerm(""))
          }
          onImportDraft={onImportDraft}
        />
      </Box>

      {/* ── Fleet overview: the one map layer without a tree row ───────── */}
      {displayLevel === "vessels" && (
        <Box sx={{ ...CARD_SX, flexShrink: 0 }}>
          <FleetOverviewCard
            routeCount={fleetPlanSummary?.routeCount ?? 0}
            vesselCount={fleetPlanSummary?.vesselCount ?? 0}
            active={!!switchFleetPlan}
            loading={!!fleetPlanSummary?.loading || !!fetchingFleetPlan}
            onToggle={onToggleFleetPlan}
          />
        </Box>
      )}

      {switchVesselFiltersActive === false && (
        <Box sx={{ ...ROW_DESCRIPTION_SX, px: 0.5, flexShrink: 0 }}>
          Hidden on map — use the eye switch to show routes and tracks
        </Box>
      )}

      {/* ── Card 2: Drill list (header fixed + rows scroll) ──────────────── */}
      <Box
        sx={{
          ...CARD_SX,
          // Fills the remaining height of the (non-scrolling) panel. Flex column
          // so the header rows stay fixed at the top and only the list region
          // (below) scrolls to absorb overflow.
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
      {/* "Back" steps one drill level up; leaving the vessel level clears
          the selection (goToVessels unmarks + clears). */}
      {displayLevel !== "vessels" && (
        <DrillHeader
          title={drilledVessel?.name ?? "Vessel"}
          meta={
            displayLevel === "voyages" ? drilledVoyageMeta : drilledVoyageName
          }
          onBack={displayLevel === "voyages" ? goToVessels : goToVoyages}
          backLabel={
            displayLevel === "voyages" ? "Back to vessels" : "Back to voyages"
          }
          canCompare={selectedRouteCount > 0}
          onCompare={onOpenRouteTable}
          specsOpen={specsVisible}
          onToggleSpecs={
            specsAvailable
              ? () => setShowVesselSpecs((prev) => !prev)
              : null
          }
        />
      )}

      {specsVisible ? (
        <VesselSpecsView
          meta={drilledVesselMeta}
          vesselLabel={drilledVessel?.name ?? ""}
          vesselId={drilledVesselId}
        />
      ) : (
        <>
      <DrillListTitle
        title={
          displayLevel === "vessels"
            ? "Vessels"
            : displayLevel === "voyages"
              ? "Voyages"
              : "Routes"
        }
      />

      {vesselsLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!!vesselsError && <Alert severity="error">{vesselsError}</Alert>}

      {/* ── Level 1: Vessels ───────────────────────────────────────────── */}
      {!vesselsLoading && !vesselsError && displayLevel === "vessels" && (
        <Box sx={LIST_SCROLL_SX} data-tour="fleet-vessels">
          <List dense disablePadding>
            {filteredVessels.map((vessel) => {
              const vesselId = vessel?.id ?? vessel?.name;
              const isActive = isSelected(vessel?.name ?? "");
              return (
                <DrillRow
                  key={vesselId}
                  icon={<DirectionsBoatIcon sx={{ fontSize: 16 }} />}
                  primary={vessel?.name ?? ""}
                  selected={isActive}
                  onClick={() => {
                    drillIntoVessel(vessel);
                    treeDispatch(treeSelectionActions.setVesselSearchTerm(""));
                  }}
                />
              );
            })}
            {filteredVessels.length === 0 && (
              <Box sx={{ px: 1, py: 1.5, fontSize: "12px", color: "#8a98a6" }}>
                No vessels found
              </Box>
            )}
          </List>
        </Box>
      )}

      {/* ── Level 2: Voyages ───────────────────────────────────────────── */}
      {!vesselsLoading && !vesselsError && displayLevel === "voyages" && (
        <Box sx={LIST_SCROLL_SX} data-tour="fleet-voyages">
          {voyagesLoadingByVesselId[drillPath.vesselId] && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={18} />
            </Box>
          )}
          {voyagesErrorByVesselId[drillPath.vesselId] && (
            <Alert severity="error">
              {voyagesErrorByVesselId[drillPath.vesselId]}
            </Alert>
          )}
          <List dense disablePadding>
            <Box sx={SECTION_HEADER_SX}>Vessel track</Box>
            {reportedRow}
            {voyagelessDrafts.length > 0 && (
              <>
                <Box sx={{ ...SECTION_HEADER_SX, mt: 1 }}>Voyage Planner</Box>
                <DrillRow
                  key="no-voyage"
                  icon={<SwipeRightAltIcon sx={{ fontSize: 16 }} />}
                  primary="New voyage"
                  badge={<StatusBadge variant="unsaved" label="Draft" />}
                  onClick={drillIntoNoVoyage}
                  action={
                    <IconButton
                      size="small"
                      aria-label="Voyage actions"
                      onClick={(event) =>
                        setNoVoyageMenuAnchorEl(event.currentTarget)
                      }
                      sx={{ color: "#004f7d", p: 0.25 }}
                    >
                      <MoreVertIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  }
                />
                <Menu
                  anchorEl={noVoyageMenuAnchorEl}
                  open={Boolean(noVoyageMenuAnchorEl)}
                  onClose={() => setNoVoyageMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    sx={{ fontSize: 13, color: "error.main" }}
                    onClick={() => {
                      setNoVoyageMenuAnchorEl(null);
                      openDeleteDialog(
                        drilledVessel,
                        null,
                        voyagelessDrafts[0],
                      );
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 28, color: "error.main" }}>
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Delete"
                      slotProps={{ primary: { sx: { fontSize: 13 } } }}
                    />
                  </MenuItem>
                </Menu>
              </>
            )}
            <Box sx={{ ...SECTION_HEADER_SX, mt: 1 }}>Voyages</Box>
            {drilledVesselHasId &&
              voyageMonthsByVesselId[drillPath.vesselId] != null &&
              !activeVoyage &&
              voyagelessDrafts.length === 0 && (
                <NewVoyageCard size="full" onPlan={() => onPlanVoyage?.(drilledVessel)} />
              )}
            {drilledVoyages.map((voyage) => {
              const voyageArchived = isRouteArchived(voyage);
              return (
              <DrillRow
                key={voyage?.id ?? voyage?.name}
                icon={<SwipeRightAltIcon sx={{ fontSize: 16 }} />}
                primary={voyage?.name ?? `Voyage ${voyage?.id ?? ""}`}
                secondary={formatUtcShort(voyage?.timestamp)}
                muted={voyageArchived}
                badge={stateBadge(voyage)}
                onClick={() => drillIntoVoyage(drilledVessel, voyage)}
                action={
                  <IconButton
                    size="small"
                    aria-label="Voyage actions"
                    onClick={(event) =>
                      handleOpenVoyageActionMenu(event, drilledVessel, voyage)
                    }
                    sx={{ color: "#004f7d", p: 0.25 }}
                  >
                    <MoreVertIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                }
              />
              );
            })}
            {drilledVesselHasId && activeVoyage && (
              <NewVoyageCard size="compact" onPlan={() => onPlanVoyage?.(drilledVessel)} />
            )}
            {drillPath.vesselId != null && (
              <Button
                fullWidth
                size="small"
                disabled={!!voyagesLoadingByVesselId[drillPath.vesselId]}
                onClick={() => loadOlderVoyagesForVessel(drillPath.vesselId)}
                startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
                sx={{
                  mt: 0.25,
                  textTransform: "none",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#004f7d",
                  justifyContent: "center",
                }}
              >
                {voyagesLoadingByVesselId[drillPath.vesselId]
                  ? "Loading…"
                  : `Load older (${
                      (voyageMonthsByVesselId[drillPath.vesselId] ??
                        VOYAGE_MONTHS_STEP) + VOYAGE_MONTHS_STEP
                    } months total)`}
              </Button>
            )}
          </List>
        </Box>
      )}

      {/* ── Level 3: Routes ────────────────────────────────────────────── */}
      {!vesselsLoading && !vesselsError && displayLevel === "routes" && (
        <Box sx={LIST_SCROLL_SX} data-tour="fleet-routes">
          {routesLoadingByVoyageId[drillPath.voyageId] && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={18} />
            </Box>
          )}
          {routesErrorByVoyageId[drillPath.voyageId] && (
            <Alert severity="error">
              {routesErrorByVoyageId[drillPath.voyageId]}
            </Alert>
          )}

          <Box sx={SECTION_HEADER_SX}>Vessel track</Box>
          <List dense disablePadding data-tour="fleet-reported">
            {reportedRow}
          </List>
          {(() => {
            const renderRouteRow = (route) => {
              const isDraft = route?.source === "draft";
              const selected = isDraft
                ? (selectedDraftRouteIds ?? []).includes(route?.routeId)
                : isRouteSelected(treeState, route?.id);
              const isReported =
                String(route?.type ?? route?.routeType ?? "").toLowerCase() ===
                "reported";
              const isArchived = isRouteArchived(route);
              const badge = (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                  {routeHasWarnings(route) && (
                    <WarningAmberIcon sx={{ fontSize: 15, color: DANGER }} />
                  )}
                  {routeHasEcaZones(route) && (
                    <WarningAmberIcon sx={{ fontSize: 15, color: CAUTION }} />
                  )}
                  {stateBadge(route)}
                </Box>
              );
              const action = !isReported && (
                <IconButton
                  size="small"
                  aria-label="Route actions"
                  data-tour="route-actions"
                  onClick={(event) =>
                    handleOpenRouteActionMenu(
                      event,
                      drilledVessel,
                      drilledVoyage,
                      route,
                    )
                  }
                  sx={{ color: "#004f7d", p: 0.25 }}
                >
                  <MoreVertIcon sx={{ fontSize: 16 }} />
                </IconButton>
              );
              const onClick = () =>
                isDraft
                  ? handleDraftClick(route)
                  : handleRouteClick(drilledVessel, drilledVoyage, route);

              if (isDraft || isReported) {
                return (
                  <DrillRow
                    key={route?.id ?? route?.name}
                    icon={<TimelineIcon sx={{ fontSize: 16 }} />}
                    primary={getRouteDisplayName(route)}
                    secondary={
                      isDraft
                        ? formatUpdatedUtc(routeUpdatedAt(route))
                        : formatUtcShort(route?.timestamp)
                    }
                    muted={isArchived}
                    selected={selected}
                    showChevron={false}
                    badge={badge}
                    onClick={onClick}
                    action={action}
                  />
                );
              }

              const routeId = Number(route?.id);
              return (
                <RouteRowCard
                  key={route?.id ?? route?.name}
                  route={route}
                  primary={getRouteDisplayName(route)}
                  secondary={formatUpdatedUtc(routeUpdatedAt(route))}
                  badgeSlot={badge}
                  actionSlot={action}
                  selected={selected}
                  muted={isArchived}
                  expanded={expandedRouteIds.has(routeId)}
                  onToggleExpanded={() => toggleRouteExpanded(routeId)}
                  onRowClick={onClick}
                  staleness={expandedRouteIds.has(routeId) ? routeStaleness(route, stalenessRef) : null}
                  onRecalculate={() =>
                    onRecalculateRoute?.({
                      vessel: drilledVessel,
                      voyage: drilledVoyage,
                      route,
                    })
                  }
                  recalculating={
                    recalculatingRouteId != null &&
                    Number(recalculatingRouteId) === routeId
                  }
                  recalcBlocked={
                    recalculatingRouteId != null &&
                    Number(recalculatingRouteId) !== routeId
                  }
                  recalcError={recalcErrorByRouteId?.[routeId] ?? null}
                  onDismissError={() => onClearRecalcError?.(routeId)}
                />
              );
            };

            const plannerRoutes = drilledRoutes.filter(
              (r) => r?.source === "draft",
            );
            const dbRoutes = drilledRoutes.filter(
              (r) => r?.source !== "draft",
            );
            return (
              <>
                {plannerRoutes.length > 0 && (
                  <>
                    <Box sx={{ ...SECTION_HEADER_SX, mt: 1 }}>Voyage Planner</Box>
                    <List dense disablePadding>
                      {plannerRoutes.map(renderRouteRow)}
                    </List>
                  </>
                )}
                {dbRoutes.length > 0 && (
                  <Box sx={{ ...SECTION_HEADER_SX, mt: 1 }}>Saved routes</Box>
                )}
                <List dense disablePadding>
                  {dbRoutes.map(renderRouteRow)}
                </List>
                {!routesLoadingByVoyageId[drillPath.voyageId] &&
                  drilledRoutes.length === 0 && (
                    <Box sx={{ px: 1, py: 1.5, fontSize: "12px", color: "#8a98a6" }}>
                      No routes
                    </Box>
                  )}
              </>
            );
          })()}
        </Box>
      )}
        </>
      )}
      </Box>

      <NotificationSnackbar
        open={!!actionError}
        message={actionError}
        autoHideDuration={6000}
        onClose={() => setActionError("")}
      />
    </Box>
  );
}
